import { createContext, useContext, type ReactNode } from "react";

/** True while the surrounding subtree presents saved values instead of capturing them. */
const ReadOnlyFieldsContext = createContext(false);

const useReadOnlyFields = () => useContext(ReadOnlyFieldsContext);

/**
 * Resolves a field's "cannot be edited" flag into the two states the common
 * field components actually render.
 *
 * A form has only ever had one way to say a field cannot be edited, so
 * "locked on an editable form" and "a value being presented on a preview"
 * collapsed into the same `disabled` prop and both rendered greyed out.
 *
 * The meaning is decided by where the field sits rather than by the call site:
 *
 * - Outside a {@link ReadOnlyFields} region the flag keeps its existing
 *   meaning — the control is `disabled` and greyed by the theme, so a field
 *   locked on an editable form still reads as locked.
 * - Inside one, the same flag means the value is being presented, so the
 *   control renders `readOnly` instead: the theme's read-only styling applies
 *   and the text stays focusable, selectable and copyable.
 *
 * Call sites keep passing `disabled` (or `readOnly`, on the selects that have
 * always used it to mean "locked") and need no per-field change.
 */
export const useFieldLock = (locked?: boolean) => {
  const presenting = useReadOnlyFields();

  return {
    disabled: !!locked && !presenting,
    readOnly: !!locked && presenting,
  };
};

type ReadOnlyFieldsProps = {
  /** Set false to render an editable form from the same components. */
  active?: boolean;
  children: ReactNode;
};

/**
 * Marks a subtree as a read-only preview: every common field inside presents
 * its value instead of greying it out.
 *
 * Wrap the preview screen once — the fields inside need no change, and neither
 * do the components shared with the editable version of the same form.
 */
const ReadOnlyFields = ({ active = true, children }: ReadOnlyFieldsProps) => (
  <ReadOnlyFieldsContext.Provider value={active}>
    {children}
  </ReadOnlyFieldsContext.Provider>
);

export default ReadOnlyFields;
